import * as THREE from 'three'
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import { viewerApi } from '@viewer/lib/viewerApi'
import { useAppStore } from '@core/store'
import { resolveWorldBox } from '@viewer/lib/geoTransform'
import { normalizeEpsg, resolverNoPonto } from './crsDefs'
import { loadProj4 } from './proj4Lazy'
import { cotaDeAssentamento, fundosDosElementos } from '@viewer/lib/kmzFederacao'

// ---------------------------------------------------------------------------
// Basemap 3D: "o modelo em cima do mapa", dentro do viewport three.js.
//
// Desenha um chão de tiles raster do OpenStreetMap por baixo do modelo
// georreferenciado, ao estilo do modo 3D do ifc-georeferencer:
//   * o modelo ativo dá o CRS (override ?? meta) e o placement
//     (override ?? baseline);
//   * o centro do bbox world → E/N → lat/lon (proj4) → grelha de tiles
//     slippy à volta;
//   * cada tile é um quad cujos 4 cantos são reprojetados de WGS84 de
//     volta ao CRS (E,N) e colocados no mundo (x=E, z=-N), à cota do
//     mínimo do bbox, pelo que a rotação/escala do modelo e a distorção
//     da projeção ficam automaticamente corretas;
//   * atribuição "© OpenStreetMap" obrigatória, como etiqueta no canto.
//
// Bounds pinados a zero (o fit da câmara ignora o chão) e raycast
// desligado (cliques atravessam para o modelo).
// ---------------------------------------------------------------------------

const GROUP_NAME = '__vn2rBasemap3d'
const GRID_BASE = 4 // grelha base de tiles; multiplicada por basemapExtent
const TILE_URL = (z: number, x: number, y: number) =>
  `https://tile.openstreetmap.org/${z}/${x}/${y}.png`

// Relevo: tiles DEM "terrarium" (Mapzen/AWS Open Data, CORS aberto).
// Altura em metros: h = (R*256 + G + B/256) - 32768. Zoom máximo útil 15
// (~30 m/px); acima disso reutiliza-se o tile 15 com bilinear.
const DEM_URL = (z: number, x: number, y: number) =>
  `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x}/${y}.png`
const DEM_MAX_Z = 15
const TERRAIN_SEG = 16 // subdivisões por tile no modo relevo

const demCache = new Map<string, Promise<ImageData | null>>()

function loadDemTile(
  z: number,
  x: number,
  y: number,
): Promise<ImageData | null> {
  const key = `${z}/${x}/${y}`
  let p = demCache.get(key)
  if (!p) {
    if (demCache.size > 256) demCache.clear()
    p = new Promise((resolve) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const c = document.createElement('canvas')
        c.width = img.width
        c.height = img.height
        const ctx = c.getContext('2d')
        if (!ctx) {
          resolve(null)
          return
        }
        ctx.drawImage(img, 0, 0)
        resolve(ctx.getImageData(0, 0, img.width, img.height))
      }
      img.onerror = () => resolve(null)
      img.src = DEM_URL(z, x, y)
    })
    demCache.set(key, p)
  }
  return p
}

function decodeTerrarium(d: ImageData, x: number, y: number): number {
  const i = (y * d.width + x) * 4
  return d.data[i] * 256 + d.data[i + 1] + d.data[i + 2] / 256 - 32768
}

/** Mensagem quando o tile do DEM não se obteve. */
export const MSG_SEM_ELEVACAO = 'Sem dados de elevação (rede indisponível?).'

/** As alturas de um tile de relevo, ou null quando falta alguma. Um
 *  vértice sem dados nunca vai a 0 m (ficava um buraco ao nível do mar no
 *  meio do relevo): quem chama desenha o tile plano ou usa o relevo do
 *  nível de cima. */
export function alturasCompletas(alturas: (number | null)[]): number[] | null {
  return alturas.every((h): h is number => h !== null && Number.isFinite(h)) ? alturas : null
}

/** Elevação (m acima do nível do mar) em lon/lat, por amostragem
 *  bilinear do tile terrarium que contém o ponto (clamp nos bordos), ou
 *  null quando o tile não se obteve (sem rede): nunca 0 m sem o saber. */
export async function elevationAtOrNull(
  lon: number,
  lat: number,
  zDem: number,
): Promise<number | null> {
  const n = 2 ** zDem
  const fx = ((lon + 180) / 360) * n
  const latR = (lat * Math.PI) / 180
  const fy =
    ((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2) * n
  const tx = Math.min(Math.max(Math.floor(fx), 0), n - 1)
  const ty = Math.min(Math.max(Math.floor(fy), 0), n - 1)
  const img = await loadDemTile(zDem, tx, ty)
  if (!img) return null
  const px = (fx - tx) * img.width - 0.5
  const py = (fy - ty) * img.height - 0.5
  const clampX = (v: number) => Math.min(Math.max(v, 0), img.width - 1)
  const clampY = (v: number) => Math.min(Math.max(v, 0), img.height - 1)
  const x0 = clampX(Math.floor(px))
  const x1 = clampX(x0 + 1)
  const y0 = clampY(Math.floor(py))
  const y1 = clampY(y0 + 1)
  const wx = Math.min(Math.max(px - x0, 0), 1)
  const wy = Math.min(Math.max(py - y0, 0), 1)
  const h00 = decodeTerrarium(img, x0, y0)
  const h10 = decodeTerrarium(img, x1, y0)
  const h01 = decodeTerrarium(img, x0, y1)
  const h11 = decodeTerrarium(img, x1, y1)
  const top = h00 * (1 - wx) + h10 * wx
  const bot = h01 * (1 - wx) + h11 * wx
  return top * (1 - wy) + bot * wy
}

const ZERO_BOX = new THREE.Box3(
  new THREE.Vector3(0, 0, 0),
  new THREE.Vector3(0, 0, 0),
)
const ZERO_SPHERE = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 0)

function pinBounds(geom: THREE.BufferGeometry): void {
  geom.boundingBox = ZERO_BOX.clone()
  geom.boundingSphere = ZERO_SPHERE.clone()
}

function clearGroup(): void {
  const world = viewerApi.getWorld()
  if (!world) return
  const scene = world.scene.three
  const g = scene.getObjectByName(GROUP_NAME)
  if (!g) return
  g.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.geometry) m.geometry.dispose()
    if (m.material) {
      const mats = Array.isArray(m.material) ? m.material : [m.material]
      for (const mat of mats) {
        const tex = (mat as THREE.MeshBasicMaterial).map
        if (tex) tex.dispose()
        mat.dispose()
      }
    }
    const css2d = o as { element?: unknown }
    if (css2d.element instanceof HTMLElement && css2d.element.parentNode) {
      css2d.element.parentNode.removeChild(css2d.element)
    }
  })
  scene.remove(g)
}

// Slippy map helpers.
function lonLatToTile(lon: number, lat: number, z: number) {
  const n = 2 ** z
  const x = Math.floor(((lon + 180) / 360) * n)
  const latR = (lat * Math.PI) / 180
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2) * n,
  )
  return { x, y }
}
function tileToLonLat(x: number, y: number, z: number) {
  const n = 2 ** z
  const lon = (x / n) * 360 - 180
  const latR = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)))
  return { lon, lat: (latR * 180) / Math.PI }
}

// Token to invalidate in-flight async builds when settings change fast.
let buildToken = 0

/** Rebuild (or remove) the OSM ground under the active georeferenced
 *  model. Reads everything from the store; safe to call repeatedly. */
export async function applyBasemap3D(visible: boolean): Promise<void> {
  const myToken = ++buildToken
  clearGroup()
  if (!visible) {
    viewerApi._invalidate()
    return
  }
  const world = viewerApi.getWorld()
  if (!world) return
  const state = useAppStore.getState()
  const modelId = state.activeModelId
  if (!modelId) return
  const model = viewerApi.getModel(modelId)
  if (!model) return
  // Com CRS conhecido, o chão desenha-se SEMPRE por baixo da pegada do
  // modelo, mesmo sem MapConversion/override (as coordenadas do
  // ficheiro valem como coordenadas de mapa no CRS escolhido; o
  // utilizador pode depois reposicionar com o clique no mapa).
  const epsgRaw = state.crsOverrides[modelId] ?? state.crsMeta[modelId] ?? null
  if (!epsgRaw) return
  const caixa = resolveWorldBox(model)
  const def = (
    await resolverNoPonto(
      normalizeEpsg(epsgRaw),
      caixa ? [(caixa.min.x + caixa.max.x) / 2, -(caixa.min.z + caixa.max.z) / 2] : null,
    )
  )?.def
  if (!def || myToken !== buildToken) return
  // proj4 sob demanda (só o chão OSM/3D e o painel de mapa o usam). Fica
  // em const local para as closures síncronas abaixo (buildFlatGeometry,
  // buildTerrainGeometry) o usarem sem mais awaits.
  const proj4 = await loadProj4()
  if (myToken !== buildToken) return

  // Bbox do modelo em coords do mundo (para centro, extensão e cota).
  const box = resolveWorldBox(model)
  if (!box) return
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  const spanM = Math.max(size.x, size.z, 30)
  // Sem relevo: chão plano à cota manual (MapPanel) ou à base do modelo.
  // A base é a da maioria dos elementos (regra do KMZ): com o ponto mais
  // baixo, os marcadores de coordenação à cota 0 punham o chão a 0 m e
  // o edifício do Cha139, a 68 m, aparecia no ar (23/10/2026).
  // Com relevo: alturas reais do DEM; "Chão h" passa a offset vertical.
  const terrainOn = state.basemapTerrainOn
  const base =
    state.basemapGroundH === null
      ? (cotaDeAssentamento(await fundosDosElementos(model))?.cota ?? box.min.y)
      : box.min.y
  if (myToken !== buildToken) return
  const groundY = state.basemapGroundH ?? base - 0.1
  const terrainOffset = state.basemapGroundH ?? 0

  // Centro em lat/lon e zoom que faz GRID tiles cobrirem ~5× o modelo.
  const E0 = center.x
  const N0 = -center.z
  let lon0: number, lat0: number
  try {
    ;[lon0, lat0] = proj4(def, 'WGS84', [E0, N0])
  } catch {
    return
  }
  if (!Number.isFinite(lat0) || !Number.isFinite(lon0)) return
  // Zoom calculado para a grelha BASE cobrir ~5× o modelo; a extensão
  // multiplica a grelha (mais tiles, mesma resolução, mais mapa).
  const extent = Math.min(3, Math.max(1, Math.round(state.basemapExtent ?? 1)))
  const GRID = GRID_BASE * extent
  const wanted = (spanM * 5) / GRID_BASE // metros por tile
  const equator = 40075016.686
  const zRaw = Math.log2(
    (equator * Math.cos((lat0 * Math.PI) / 180)) / wanted,
  )
  const z = Math.max(13, Math.min(19, Math.round(zRaw)))
  const { x: cx, y: cy } = lonLatToTile(lon0, lat0, z)

  const group = new THREE.Group()
  group.name = GROUP_NAME
  group.raycast = () => {}
  world.scene.three.add(group)

  const loader = new THREE.TextureLoader()
  loader.setCrossOrigin('anonymous')

  const zDem = Math.min(z, DEM_MAX_Z)

  // Quad plano: 4 cantos do tile reprojetados, tudo à cota groundY.
  const buildFlatGeometry = (
    tx: number,
    ty: number,
  ): THREE.BufferGeometry | null => {
    const nw = tileToLonLat(tx, ty, z)
    const ne = tileToLonLat(tx + 1, ty, z)
    const se = tileToLonLat(tx + 1, ty + 1, z)
    const sw = tileToLonLat(tx, ty + 1, z)
    let pts: Array<[number, number]>
    try {
      pts = [nw, ne, se, sw].map(({ lon, lat }) => {
        const [E, N] = proj4('WGS84', def, [lon, lat])
        return [E, N] as [number, number]
      })
    } catch {
      return null
    }
    const verts = new Float32Array([
      pts[0][0], groundY, -pts[0][1], // NW
      pts[1][0], groundY, -pts[1][1], // NE
      pts[2][0], groundY, -pts[2][1], // SE
      pts[3][0], groundY, -pts[3][1], // SW
    ])
    const uv = new Float32Array([0, 1, 1, 1, 1, 0, 0, 0])
    const geom = new THREE.BufferGeometry()
    geom.setAttribute('position', new THREE.BufferAttribute(verts, 3))
    geom.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
    geom.setIndex([0, 3, 2, 0, 2, 1])
    geom.computeVertexNormals()
    pinBounds(geom)
    return geom
  }

  // Grelha SEG×SEG com alturas do DEM em cada vértice. Os vértices dos
  // bordos usam a mesma função global de amostragem, pelo que tiles
  // vizinhos encaixam sem fendas.
  const buildTerrainGeometry = async (
    tx: number,
    ty: number,
  ): Promise<THREE.BufferGeometry | null> => {
    const seg = TERRAIN_SEG
    const count = (seg + 1) * (seg + 1)
    const verts = new Float32Array(count * 3)
    const uv = new Float32Array(count * 2)
    const lidas = await Promise.all(
      Array.from({ length: count }, (_, k) => {
        const i = k % (seg + 1)
        const j = Math.floor(k / (seg + 1))
        const { lon, lat } = tileToLonLat(tx + i / seg, ty + j / seg, z)
        return elevationAtOrNull(lon, lat, zDem)
      }),
    )
    // Sem dados de elevação para este tile: fica plano à cota do chão,
    // em vez de cair ao nível do mar.
    const heights = alturasCompletas(lidas)
    if (!heights) return buildFlatGeometry(tx, ty)
    try {
      for (let j = 0; j <= seg; j++) {
        for (let i = 0; i <= seg; i++) {
          const k = j * (seg + 1) + i
          const { lon, lat } = tileToLonLat(tx + i / seg, ty + j / seg, z)
          const [E, N] = proj4('WGS84', def, [lon, lat])
          verts[k * 3] = E
          verts[k * 3 + 1] = heights[k] + terrainOffset
          verts[k * 3 + 2] = -N
          uv[k * 2] = i / seg
          uv[k * 2 + 1] = 1 - j / seg
        }
      }
    } catch {
      return null
    }
    const index: number[] = []
    for (let j = 0; j < seg; j++) {
      for (let i = 0; i < seg; i++) {
        const a = j * (seg + 1) + i
        const b = a + 1
        const c = a + (seg + 1)
        const d = c + 1
        index.push(a, c, d, a, d, b)
      }
    }
    const geom = new THREE.BufferGeometry()
    geom.setAttribute('position', new THREE.BufferAttribute(verts, 3))
    geom.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
    geom.setIndex(index)
    geom.computeVertexNormals()
    pinBounds(geom)
    return geom
  }

  const half = Math.floor(GRID / 2)
  const promises: Promise<void>[] = []
  for (let dx = -half; dx < GRID - half; dx++) {
    for (let dy = -half; dy < GRID - half; dy++) {
      const tx = cx + dx
      const ty = cy + dy
      promises.push(
        (async () => {
          const geom = terrainOn
            ? await buildTerrainGeometry(tx, ty)
            : buildFlatGeometry(tx, ty)
          if (!geom) return
          if (myToken !== buildToken) {
            geom.dispose()
            return
          }
          await new Promise<void>((resolve) => {
            loader.load(
              TILE_URL(z, tx, ty),
              (tex) => {
                if (myToken !== buildToken) {
                  tex.dispose()
                  geom.dispose()
                  resolve()
                  return
                }
                tex.colorSpace = THREE.SRGBColorSpace
                const mat = new THREE.MeshBasicMaterial({
                  map: tex,
                  side: THREE.DoubleSide,
                })
                const mesh = new THREE.Mesh(geom, mat)
                mesh.frustumCulled = false
                mesh.renderOrder = -1
                mesh.raycast = () => {}
                group.add(mesh)
                viewerApi._invalidate()
                resolve()
              },
              undefined,
              () => {
                geom.dispose()
                resolve() // tile falhou: sem rede/limite, segue
              },
            )
          })
        })(),
      )
    }
  }
  await Promise.all(promises)
  if (myToken !== buildToken) return

  // Atribuição obrigatória do OpenStreetMap (+ fonte do relevo), no canto
  // SE do chão.
  const attr = document.createElement('div')
  attr.textContent = terrainOn
    ? '© OpenStreetMap contributors · Relevo: Mapzen/AWS terrain tiles'
    : '© OpenStreetMap contributors'
  attr.style.color = '#e5e7eb'
  attr.style.fontSize = '10px'
  attr.style.padding = '1px 4px'
  attr.style.background = 'rgba(11,11,12,0.6)'
  attr.style.borderRadius = '3px'
  attr.style.pointerEvents = 'none'
  attr.style.whiteSpace = 'nowrap'
  const attrObj = new CSS2DObject(attr)
  const seCorner = tileToLonLat(cx + (GRID - half), cy + (GRID - half), z)
  const hCanto = terrainOn ? await elevationAtOrNull(seCorner.lon, seCorner.lat, zDem) : null
  const attrY = hCanto !== null ? hCanto + terrainOffset : groundY
  try {
    const [E, N] = proj4('WGS84', def, [seCorner.lon, seCorner.lat])
    attrObj.position.set(E, attrY, -N)
  } catch {
    attrObj.position.set(center.x, attrY, center.z)
  }
  group.add(attrObj)
  viewerApi._invalidate()
}
